import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { assertNever } from '@/utils/assert';
import {
  CURSOR_BODY_ATTR,
  CursorContext,
  DEFAULT_CURSOR_MODE,
  type CursorController,
  type CursorMode,
} from './useCursorMode';

export function CursorProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<CursorMode>(DEFAULT_CURSOR_MODE);

  const setMode = useCallback((m: CursorMode) => {
    switch (m) {
      case 'default':
      case 'hidden':
      case 'precision':
      case 'crosshair':
        setModeState(m);
        return;
      default:
        assertNever(m);
    }
  }, []);

  const reset = useCallback(() => {
    setModeState(DEFAULT_CURSOR_MODE);
  }, []);

  useEffect(() => {
    document.body.setAttribute(CURSOR_BODY_ATTR, mode);
    return () => {
      document.body.removeAttribute(CURSOR_BODY_ATTR);
    };
  }, [mode]);

  const value = useMemo<CursorController>(
    () => ({ mode, setMode, reset }),
    [mode, setMode, reset]
  );

  return <CursorContext.Provider value={value}>{children}</CursorContext.Provider>;
}
